import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin } from 'lucide-react';
import { SiX, SiFacebook, SiInstagram, SiYoutube } from '@icons-pack/react-simple-icons';
import { trackNavClick, trackOutboundClick } from '../lib/analytics';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Native <a href> links only activate on Enter. This restores Space-key
  // activation (matching the previous <button> behaviour) without affecting
  // Enter, which continues to work via the existing onClick handlers.
  const handleSectionKeyDown = (e, action) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      action();
    }
  };

  const footerLinks = {
    product: [
      { label: 'Features', type: 'section', target: 'solutions' },
      { label: 'Integrations', type: 'section', target: 'integrations' },
      { label: 'Pricing', type: 'section', target: 'pricing' },
      { label: 'Case Studies', type: 'section', target: 'case-studies' },
      { label: 'Guest/Attendee App', type: 'route', target: '/apps' },
    ],
    solutions: [
      { label: 'Cashless Payments', type: 'section', target: 'solutions' },
      { label: 'Loyalty & Membership', type: 'section', target: 'solutions' },
      { label: 'Access & Experience Control', type: 'section', target: 'solutions' },
      { label: 'Guest Profiles & Insights', type: 'section', target: 'solutions' },
    ],
    company: [
      { label: 'Contact', type: 'section', target: 'demo' },
      { label: 'Support', type: 'route', target: '/support' },
    ],
    legal: [
      { label: 'Privacy Policy', type: 'route', target: '/privacy-policy' },
      { label: 'Terms & Conditions', type: 'route', target: '/terms-conditions' },
    ],
  };

  const renderLink = (link) => {
    const className = 'text-gray-400 hover:text-[#009688] transition-colors text-sm';
    if (link.type === 'route') {
      return (
        <Link
          to={link.target}
          onClick={() => trackNavClick(link.label, 'footer')}
          className={className}
        >
          {link.label}
        </Link>
      );
    }
    if (link.type === 'section') {
      return (
        <a
          href={`/#${link.target}`}
          onClick={(e) => {
            e.preventDefault();
            trackNavClick(link.label, 'footer');
            goToSection(link.target);
          }}
          onKeyDown={(e) =>
            handleSectionKeyDown(e, () => {
              trackNavClick(link.label, 'footer');
              goToSection(link.target);
            })
          }
          className={`${className} text-left inline-block`}
        >
          {link.label}
        </a>
      );
    }
    return (
      <a
        href={link.target}
        onClick={() => trackNavClick(link.label, 'footer')}
        className={className}
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className="bg-[#101F27] border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/">
              <img
                src="/images/wristcoin_logo.png"
                alt="WristCoin"
                className="h-10 w-auto mb-4"
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              Empowering seamless, secure, and efficient cashless transactions for hospitality and events worldwide.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-400 hover:text-[#009688] transition-colors">
                <Mail size={18} />
                <span className="text-sm">info@mywristcoin.com</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400">
                <MapPin size={18} />
                <span className="text-sm">108 College St Suite W754, Toronto, Canada M5G 0C6</span>
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <p className="text-white font-semibold mb-4">Product</p>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <p className="text-white font-semibold mb-4">Solutions</p>
            <ul className="space-y-2">
              {footerLinks.solutions.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <p className="text-white font-semibold mb-4">Company</p>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {new Date().getFullYear()} WristCoin. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-6">
              <a
                href="https://www.youtube.com/@wristcoin"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('https://www.youtube.com/@wristcoin', 'YouTube', 'footer_social')}
                className="text-gray-400 hover:text-[#009688] transition-colors"
                aria-label="YouTube"
              >
                <SiYoutube size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/wristcoin/ "
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('https://www.linkedin.com/company/wristcoin/', 'LinkedIn', 'footer_social')}
                className="text-gray-400 hover:text-[#009688] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.instagram.com/mywristcoin/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('https://www.instagram.com/mywristcoin/', 'Instagram', 'footer_social')}
                className="text-gray-400 hover:text-[#009688] transition-colors"
                aria-label="Instagram"
              >
                <SiInstagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/mywristcoin"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('https://www.facebook.com/mywristcoin', 'Facebook', 'footer_social')}
                className="text-gray-400 hover:text-[#009688] transition-colors"
                aria-label="Facebook"
              >
                <SiFacebook size={20} />
              </a>
              <a
                href="https://x.com/mywristcoin"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutboundClick('https://x.com/mywristcoin', 'Twitter', 'footer_social')}
                className="text-gray-400 hover:text-[#009688] transition-colors"
                aria-label="Twitter"
              >
                <SiX size={20} />
              </a>
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6">
              {footerLinks.legal.map((link) => (
                <a
                  key={link.label}
                  href={link.target}
                  className="text-gray-400 hover:text-[#009688] transition-colors text-sm"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
